import { useState } from "react";
import type { FormEvent } from "react";

import { APIErrorException } from "@/core/services";
import { useDialogForm, useDialogState, useToast } from "@/ds";
import { useChaveIdempotencia, useConfirmarDescarte } from "@/shared/hooks";
import {
  formatarStatusFinanceiro,
  previaProjecaoTransferenciaTipo,
  transferirTipoObra,
} from "@/modules/financeiro-obras";
import type {
  DecisaoProjecaoTransferencia,
  LookupRequestOptions,
  ObraDestinoTipo,
  TransferirTipoObraRequest,
} from "@/modules/financeiro-obras";
import type { ColunaDestino } from "../etapa-destino-transferencia";
import {
  useDetalheEscopoMacroDados,
  useObrasDestinoTransferenciaTipoDados,
  useRevisaoTransferenciaTipoDados,
} from "../../../../hooks/use-planejamento-dados";
import {
  SUCESSOR_AJUDA,
  SUCESSOR_DIFERENTE_AJUDA,
  SUCESSOR_LABEL,
  SUCESSOR_PLACEHOLDER,
  SUCESSOR_SUGERIDO_SUFIXO,
  SUCESSOR_UNICO_AJUDA,
} from "../cancelar-tipo-obra-dialog/cancelar-tipo-obra-dialog.constants";
import {
  DOMINANTE_LABEL,
  DOMINANTE_TOOLTIP,
  MANTER_DESCRICAO,
  MANTER_LABEL,
  TROCAR_DESCRICAO,
  TROCAR_PARA_LABEL,
} from "../novo-tipo-obra-dialog/novo-tipo-obra-dialog.constants";
import { transferenciaEmFelZero } from "../planejamento-controle.cancelamento";
import { AVISO_TRANSFERENCIA_FEL_ZERO, NO_VALUE } from "../planejamento-controle.constants";
import { formatMilhoes, formatMoeda } from "../planejamento-controle.format";
import { origemDaProjecao } from "../projecao-transferencia";
import { JUSTIFICATIVA_OBRIGATORIA } from "@/shared/components";
import type { DominanciaBlocoProps } from "../dominancia-bloco";
import {
  CENTRO_CUSTO_OBRIGATORIO,
  DESTINO_OBRIGATORIO,
  COLUNA_CONDICAO,
  COLUNA_ID,
  COLUNA_INICIO,
  COLUNA_OUTLOOK,
  COLUNA_SITUACAO,
  COLUNA_STATUS,
  COLUNA_TERMINO,
  COLUNA_TIPOS,
  DESTINO_SUBTITLE,
  DESTINO_TITLE,
  EMBARCACAO_NAO_INFORMADA,
  ENVIADO_PARA_APROVACAO,
  TRANSFERIDO_MESSAGE,
  ERRO_ENVIO_PADRAO,
  ETAPA_DESTINO,
  ETAPA_REVISAO,
  INDISPONIVEL_LABEL,
  AVISO_EMBARCACAO,
  AVISO_ESCOLHA_OBRA,
  AVISO_APROVACAO_TRANSFERENCIA,
  PERGUNTA_CONFIRMACAO,
  AVISO_SEM_OBRA,
  SEM_CENTRO_CUSTO,
  SITUACAO_DISPONIVEL,
  TABELA_LABEL,
  TIPO_JA_EXISTE_AJUDA,
  TIPO_ROTULO,
  TRANSFERIR_TIPO_FORM_ID,
} from "./transferir-tipo-obra-dialog.constants";
import {
  linhasDaRevisao,
  periodoNoDestino,
  totalDaRevisao,
} from "./revisao-transferencia-tipo/revisao-transferencia-tipo.format";
import {
  corpoDosEscopos,
  useRevisaoProjecaoTransferenciaTipo,
} from "./use-revisao-projecao-transferencia-tipo";
import {
  CENTRO_CUSTO_LABEL as CENTRO_REVISAO_LABEL,
  DOMINANCIA_DESTINO_TITLE,
  DOMINANCIA_ORIGEM_TITLE,
  IMPEDIDO_AVISO,
  INCORPORACAO,
  JUSTIFICATIVA_SUCESSOR_OBRIGATORIA,
  JUSTIFICATIVA_TROCA_OBRIGATORIA,
  AVISO_REVISAO,
  PENDENCIA_DURACAO,
  REVISAR_ERRO,
  REVISAR_ROTULO_CARTAO,
  REVISAR_SEM_PROJECAO,
  SUCESSOR_JUSTIFICATIVA_LABEL,
  SUCESSOR_OBRIGATORIO,
  TIPO_CRIADO_NO_DESTINO,
  TROCA_JUSTIFICATIVA_LABEL,
} from "./revisao-transferencia-tipo/revisao-transferencia-tipo.constants";
import type {
  ContextoRevisao,
  DecisaoDominanciaRevisao,
  RevisaoTransferenciaTipoProps,
} from "./revisao-transferencia-tipo/types-revisao-transferencia-tipo";
import type {
  EtapaTransferenciaTipo,
  TransferirTipoEstado,
  TransferirTipoObraDialogProps,
} from "./types-transferir-tipo-obra-dialog";

type Etapa = 1 | 2;
type DecisaoDestino = "MANTER" | "TROCAR";

const rotuloCentro = (centro: { codigo: string; nome: string }) =>
  `${centro.nome} · ${centro.codigo}`;
const hifenTipo = (codigo: string, nome: string) => `${codigo} - ${nome}`;
const rotulo = (item: { codigo: string; nome: string }) => `${item.codigo} · ${item.nome}`;

export function useTransferirTipoObraDialog({
  obraId,
  tipo,
  onClose,
  onConcluido,
  onRecarregar,
}: TransferirTipoObraDialogProps) {
  const toast = useToast();
  const { comChave } = useChaveIdempotencia();
  const consulta = useObrasDestinoTransferenciaTipoDados(obraId, tipo.obraTipoObraId);
  const dados = consulta.data;
  const obras: ObraDestinoTipo[] = dados?.obras ?? [];
  const tipoGet = dados?.tipoObra ?? null;
  const codigoTipo = tipoGet?.codigo ?? tipo.codigo;
  const nomeTipo = tipoGet?.nome ?? tipo.nome;
  const quantidadeEscopos = tipoGet?.quantidadeEscopos ?? tipo.quantidadeEscopos;
  const valorTipo = tipoGet ? formatMoeda(tipoGet.valorNaOrigem, "BRL") : tipo.valorTipo;
  const centroCustoTipo = tipoGet
    ? `${tipoGet.centroCusto.nome} · ${tipoGet.centroCusto.codigo}`
    : tipo.centroCusto;
  const embarcacao =
    dados?.embarcacao?.nome ?? tipo.obraOrigem.embarcacao ?? EMBARCACAO_NAO_INFORMADA;

  const [etapa, setEtapa] = useState<Etapa>(1);
  const [destinoId, setDestinoId] = useState<string | null>(null);
  const [centroCustoId, setCentroCustoId] = useState("");
  const [sucessorId, setSucessorId] = useState("");
  const [justificativaSucessor, setJustificativaSucessor] = useState("");
  const [decisaoDestino, setDecisaoDestino] = useState<DecisaoDestino>("MANTER");
  const [justificativaTroca, setJustificativaTroca] = useState("");
  const [erroRevisao, setErroRevisao] = useState<string | null>(null);
  const [revisandoId, setRevisandoId] = useState<string | null>(null);
  const [tentouAvancar, setTentouAvancar] = useState(false);
  const [tentouTransferir, setTentouTransferir] = useState(false);
  const confirmacao = useDialogState();
  const descarte = useConfirmarDescarte({
    onDescartar: onClose,
    testId: "transferir-tipo-descarte",
  });

  const destino = obras.find((obra) => obra.id === destinoId) ?? null;
  const felDestino = destino ? formatarStatusFinanceiro({ codigo: destino.fel }) : null;
  const mostrarAvisoFelZero = transferenciaEmFelZero(tipo.obraOrigem.fel, felDestino);
  const centros = destino?.centrosCustoReceptores ?? [];
  const centro = centros.find((item) => item.id === centroCustoId) ?? null;
  const semCentroCusto = destino !== null && centros.length === 0;
  const destinoEscolhido = destino !== null && !semCentroCusto && centro !== null;
  const semObraDisponivel = obras.length > 0 && obras.every((obra) => !obra.disponivel);

  let estado: TransferirTipoEstado = "pronto";
  if (consulta.status === "forbidden") estado = "proibido";
  else if (consulta.status === "error") estado = "erro";
  else if (consulta.status === "loading" || !dados) estado = "carregando";
  else if (obras.length === 0) estado = "vazio";

  const revisao = useRevisaoTransferenciaTipoDados(
    obraId,
    tipo.obraTipoObraId,
    etapa === 2 && destino && centro
      ? { obraDestinoId: destino.id, centroCustoDestinoId: centro.id }
      : null,
  );
  const analise = revisao.data;
  const escopoIds = (analise?.escopos ?? []).map((escopo) => escopo.obraEscopoMacroId);
  const projecao = useRevisaoProjecaoTransferenciaTipo({
    obraId,
    obraTipoObraId: tipo.obraTipoObraId,
    destinoId,
    centroCustoId: centro?.id ?? null,
    escopoIds,
    ativa: etapa === 2 && revisao.status === "success",
  });
  const detalheRevisar = useDetalheEscopoMacroDados(
    obraId,
    revisandoId ?? "",
    revisandoId !== null,
  );
  const origemRevisar = origemDaProjecao(detalheRevisar.data?.projecao);
  const carregandoRevisarId =
    revisandoId !== null && detalheRevisar.status === "loading" ? revisandoId : null;
  let erroRevisar: string | null = null;
  if (revisandoId !== null) {
    if (detalheRevisar.status === "error" || detalheRevisar.status === "forbidden") {
      erroRevisar = REVISAR_ERRO;
    } else if (detalheRevisar.status === "success" && !origemRevisar) {
      erroRevisar = REVISAR_SEM_PROJECAO;
    }
  }

  function selecionarObra(id: string) {
    const obra = obras.find((item) => item.id === id);
    if (!obra?.disponivel || obra.id === destinoId) return;
    setDestinoId(obra.id);
    setCentroCustoId(
      obra.centrosCustoReceptores.length === 1 ? (obra.centrosCustoReceptores[0]?.id ?? "") : "",
    );
    zerarDestino();
  }

  function escolherCentro(id: string) {
    if (id === centroCustoId) return;
    setCentroCustoId(id);
    zerarDestino();
  }

  function zerarDestino() {
    setRevisandoId(null);
    setSucessorId("");
    setJustificativaSucessor("");
    setDecisaoDestino("MANTER");
    setJustificativaTroca("");
    setErroRevisao(null);
    setTentouTransferir(false);
  }

  const origemDom = analise?.dominanciaOrigem;
  const candidatos = origemDom?.aplica ? origemDom.candidatos : [];
  const sugeridoId = origemDom?.sugeridoObraTipoObraId ?? null;
  const exigeSucessor = candidatos.length > 0;
  const sucessorEscolhido = candidatos.some((item) => item.obraTipoObraId === sucessorId)
    ? sucessorId
    : (sugeridoId ?? "");
  const outroSucessor =
    exigeSucessor && sucessorEscolhido !== "" && sucessorEscolhido !== sugeridoId;
  const faltaSucessor = exigeSucessor && sucessorEscolhido === "";
  const faltaJustificativaSucessor = outroSucessor && justificativaSucessor.trim() === "";

  const destinoDom = analise?.dominanciaDestino;
  const decideDestino = destinoDom?.aplica === true;
  const trocando = decideDestino && decisaoDestino === "TROCAR";
  const faltaJustificativaTroca = trocando && justificativaTroca.trim() === "";

  const impedido = (analise?.escopos ?? []).some((escopo) => escopo.impedimentos.length > 0);
  const revisaoPronta = etapa === 2 && revisao.status === "success" && analise !== null;
  const transferirDesabilitado = !revisaoPronta || projecao.estado !== "pronto" || impedido;
  const camposInvalidos = faltaSucessor || faltaJustificativaSucessor || faltaJustificativaTroca;

  function montarCorpo(justificativa: string): TransferirTipoObraRequest | null {
    if (!destino || !centro) return null;
    const escopos = corpoDosEscopos(escopoIds, projecao.decisoes);
    const sucessor = exigeSucessor
      ? {
          sucessorObraTipoObraId: sucessorEscolhido,
          ...(outroSucessor ? { justificativaSucessor: justificativaSucessor.trim() } : {}),
        }
      : {};
    const dominanciaDestino = !decideDestino
      ? {}
      : trocando
        ? {
            decisaoDominanciaDestino: "TROCAR" as const,
            justificativaTroca: justificativaTroca.trim(),
          }
        : { decisaoDominanciaDestino: "MANTER" as const };
    return {
      obraDestinoId: destino.id,
      centroCustoDestinoId: centro.id,
      justificativa,
      escopos,
      ...sucessor,
      ...dominanciaDestino,
    } as TransferirTipoObraRequest;
  }

  const form = useDialogForm<{ justificativa: string }>({
    initial: { justificativa: "" },
    validate: (valores) =>
      valores.justificativa.trim() === "" ? { justificativa: JUSTIFICATIVA_OBRIGATORIA } : {},
    onSubmit: async (valores) => {
      const body = montarCorpo(valores.justificativa.trim());
      if (!body) return;
      let resposta;
      try {
        resposta = await comChave(body, (idempotencyKey) =>
          transferirTipoObra(obraId, tipo.obraTipoObraId, body, { idempotencyKey }),
        );
      } catch (erro) {
        if (erro instanceof APIErrorException && erro.status === 400) {
          confirmacao.close();
          setErroRevisao(erro.message || ERRO_ENVIO_PADRAO);
          revisao.reload();
          return;
        }
        if (erro instanceof APIErrorException && erro.status === 409) onRecarregar();
        throw erro;
      }
      if (resposta.efetivada) toast.success(TRANSFERIDO_MESSAGE);
      else toast.info(ENVIADO_PARA_APROVACAO);
      onConcluido();
    },
  });

  const etapas: EtapaTransferenciaTipo[] = [
    {
      numero: 1,
      titulo: ETAPA_DESTINO,
      situacao: etapa === 1 ? "active" : "done",
      ariaLabel: `Etapa 1: ${ETAPA_DESTINO}`,
      testId: "transferir-tipo-etapa-1",
      onSelect: etapa === 2 ? () => setEtapa(1) : undefined,
    },
    {
      numero: 2,
      titulo: ETAPA_REVISAO,
      situacao: etapa === 2 ? "active" : "pending",
      ariaLabel: `Etapa 2: ${ETAPA_REVISAO}`,
      testId: "transferir-tipo-etapa-2",
      onSelect: undefined,
    },
  ];

  const origem: ContextoRevisao = {
    obra: `Obra ${tipo.obraOrigem.codigo} · ${tipo.obraOrigem.fel}`,
    embarcacao: tipo.obraOrigem.embarcacao ?? EMBARCACAO_NAO_INFORMADA,
    tipoObra: hifenTipo(codigoTipo, nomeTipo),
    centroCusto: centroCustoTipo,
  };
  const paraContexto: ContextoRevisao | null = destino
    ? {
        obra: `Obra ${destino.codigo} · ${felDestino}`,
        embarcacao,
        tipoObra: hifenTipo(codigoTipo, nomeTipo),
        centroCusto: centro ? rotuloCentro(centro) : CENTRO_REVISAO_LABEL,
      }
    : null;

  const textoSucessorAjuda = () => {
    if (candidatos.length === 1) return SUCESSOR_UNICO_AJUDA;
    const sugerido = candidatos.find((item) => item.obraTipoObraId === sugeridoId);
    return outroSucessor && sugerido ? SUCESSOR_DIFERENTE_AJUDA(rotulo(sugerido)) : SUCESSOR_AJUDA;
  };

  let dominanciaOrigem: DecisaoDominanciaRevisao | null = null;
  if (exigeSucessor) {
    const bloco: DominanciaBlocoProps = {
      titulo: { texto: DOMINANCIA_ORIGEM_TITLE, ajuda: DOMINANTE_TOOLTIP },
      aviso: null,
      cartoes: null,
      seletor: {
        label: SUCESSOR_LABEL,
        placeholder: SUCESSOR_PLACEHOLDER,
        value: sucessorEscolhido || null,
        opcoes: candidatos.map((item) => ({
          label: `${rotulo(item)}${item.obraTipoObraId === sugeridoId ? ` ${SUCESSOR_SUGERIDO_SUFIXO}` : ""}`,
          value: item.obraTipoObraId,
        })),
        ajuda: textoSucessorAjuda(),
        error: tentouTransferir && faltaSucessor ? SUCESSOR_OBRIGATORIO : null,
        travado: candidatos.length === 1,
        onChange: (id: string) => setSucessorId(id),
        testId: "transferir-tipo-sucessor",
        errorTestId: "transferir-tipo-sucessor-error",
      },
      disabled: false,
      testId: "transferir-tipo-dominancia-origem",
    };
    dominanciaOrigem = {
      bloco,
      justificativa: outroSucessor
        ? {
            label: SUCESSOR_JUSTIFICATIVA_LABEL,
            value: justificativaSucessor,
            error:
              tentouTransferir && faltaJustificativaSucessor
                ? JUSTIFICATIVA_SUCESSOR_OBRIGATORIA
                : null,
            onChange: setJustificativaSucessor,
            testIds: {
              campo: "transferir-tipo-justificativa-sucessor",
              contador: "transferir-tipo-contador-sucessor",
              erro: "transferir-tipo-justificativa-sucessor-error",
            },
          }
        : null,
    };
  }

  let dominanciaDestino: DecisaoDominanciaRevisao | null = null;
  if (decideDestino && destinoDom) {
    const atual = destinoDom.dominanteAtual;
    const recebido = hifenTipo(codigoTipo, nomeTipo);
    dominanciaDestino = {
      bloco: {
        titulo: { texto: DOMINANCIA_DESTINO_TITLE, ajuda: DOMINANTE_TOOLTIP },
        aviso: null,
        cartoes: {
          itens: [
            {
              value: "MANTER",
              titulo: MANTER_LABEL,
              destaque: atual ? rotulo(atual) : null,
              descricao: MANTER_DESCRICAO,
              testId: "transferir-tipo-manter",
              destaqueTestId: "transferir-tipo-dominante-atual",
            },
            {
              value: "TROCAR",
              titulo: TROCAR_PARA_LABEL,
              destaque: recebido,
              descricao: TROCAR_DESCRICAO(
                atual ? `${codigoTipo} vem antes de ${atual.codigo}` : codigoTipo,
              ),
              testId: "transferir-tipo-trocar",
              destaqueTestId: "transferir-tipo-dominante-novo",
            },
          ],
          value: decisaoDestino,
          onChange: (valor: string) => setDecisaoDestino(valor as DecisaoDestino),
          testId: "transferir-tipo-dominancia",
          label: DOMINANTE_LABEL,
        },
        seletor: null,
        disabled: false,
        testId: "transferir-tipo-dominancia-destino",
      },
      justificativa: trocando
        ? {
            label: TROCA_JUSTIFICATIVA_LABEL,
            value: justificativaTroca,
            error:
              tentouTransferir && faltaJustificativaTroca ? JUSTIFICATIVA_TROCA_OBRIGATORIA : null,
            onChange: setJustificativaTroca,
            testIds: {
              campo: "transferir-tipo-justificativa-troca",
              contador: "transferir-tipo-contador-troca",
              erro: "transferir-tipo-justificativa-troca-error",
            },
          }
        : null,
    };
  }

  function revisarEscopo(id: string) {
    if (id === revisandoId) detalheRevisar.reload();
    else setRevisandoId(id);
  }

  const escopoRevisado = analise?.escopos.find(
    (escopo) => escopo.obraEscopoMacroId === revisandoId,
  );
  const ajustar =
    revisandoId && escopoRevisado && origemRevisar && destino && centro
      ? {
          variante: "tipo" as const,
          chavePrevia: `${obraId}/${tipo.obraTipoObraId}/${revisandoId}/${destino.id}/${centro.id}`,
          buscarPrevia: async (
            decisao: DecisaoProjecaoTransferencia | null,
            opcoes: LookupRequestOptions,
          ) => {
            const resposta = await previaProjecaoTransferenciaTipo(
              obraId,
              tipo.obraTipoObraId,
              {
                obraDestinoId: destino.id,
                centroCustoDestinoId: centro.id,
                escopos: corpoDosEscopos([revisandoId], decisao ? { [revisandoId]: decisao } : {}),
              },
              opcoes,
            );
            return { projecao: resposta.escopos[0]?.projecao ?? null, moedasSemCotacao: [] };
          },
          cartao: {
            rotulo: REVISAR_ROTULO_CARTAO,
            nome: escopoRevisado.nome,
            tipoObraCodigo: codigoTipo,
            classificacao: escopoRevisado.classificacao ?? NO_VALUE,
            projecao: null,
            contexto: [
              hifenTipo(codigoTipo, nomeTipo),
              `Obra ${tipo.obraOrigem.codigo} → Obra ${destino.codigo}`,
              rotuloCentro(centro),
              periodoNoDestino(escopoRevisado.periodoDestino, "cartao"),
            ].join(" · "),
          },
          origem: origemRevisar,
          decisao: projecao.decisoes[revisandoId] ?? null,
          onAplicar: (decisao: DecisaoProjecaoTransferencia | null) => {
            projecao.aplicarDecisao(revisandoId, decisao);
            setRevisandoId(null);
          },
          onClose: () => setRevisandoId(null),
        }
      : null;

  const revisaoView: RevisaoTransferenciaTipoProps | null =
    revisaoPronta && analise && paraContexto
      ? {
          aviso: AVISO_REVISAO,
          origem,
          destino: paraContexto,
          incorporacao: analise.tipoReceptor
            ? INCORPORACAO(analise.tipoReceptor.codigo, analise.tipoReceptor.nome)
            : TIPO_CRIADO_NO_DESTINO,
          escopos: linhasDaRevisao(analise.escopos, {
            avisoDuracao: PENDENCIA_DURACAO,
            decisoes: projecao.decisoes,
            previas: projecao.previas,
            carregandoRevisarId,
            revisar: revisarEscopo,
          }),
          total: totalDaRevisao(analise.escopos, projecao.previas),
          avisoImpedido: impedido ? IMPEDIDO_AVISO : null,
          previa: {
            carregando: projecao.estado === "carregando",
            erro: projecao.errorMessage,
            recarregar: projecao.recarregar,
          },
          erroRevisar,
          financeiro: {
            outlookOrigem: tipo.outlookOrigem,
            outlookDestino: formatMilhoes(analise.outlookDestino),
            valorTipoOrigem: valorTipo ?? NO_VALUE,
            valorTipoDestino: NO_VALUE,
          },
          dominanciaOrigem,
          dominanciaDestino,
        }
      : null;

  function cancelar() {
    descarte.pedir(destinoId !== null);
  }

  return {
    formId: TRANSFERIR_TIPO_FORM_ID,
    estado,
    errorMessage: consulta.errorMessage,
    recarregar: consulta.reload,
    etapa,
    etapas,
    cartao: {
      rotulo: TIPO_ROTULO,
      nome: hifenTipo(codigoTipo, nomeTipo),
      tipoObraCodigo: codigoTipo,
      classificacao: tipo.classificacao,
      projecao: valorTipo,
      contexto: [
        `${quantidadeEscopos} ${quantidadeEscopos === 1 ? "escopo macro" : "escopos macro"}`,
        `Obra ${tipo.obraOrigem.codigo}`,
        embarcacao,
        centroCustoTipo,
      ].join(" · "),
    },
    avisoSemObras: AVISO_SEM_OBRA(embarcacao),
    etapaDestino: {
      titulo: DESTINO_TITLE,
      subtitulo: DESTINO_SUBTITLE(hifenTipo(codigoTipo, nomeTipo)),
      aviso: `${AVISO_EMBARCACAO(embarcacao, tipo.obraOrigem.codigo)} ${AVISO_ESCOLHA_OBRA}`,
      ariaLabel: TABELA_LABEL,
      obras,
      colunas: ["status", "tipos", "condicao", "inicio", "termino", "outlook"] as ColunaDestino[],
      textos: {
        colunaId: COLUNA_ID,
        colunaSituacao: COLUNA_SITUACAO,
        colunas: {
          status: COLUNA_STATUS,
          tipos: COLUNA_TIPOS,
          condicao: COLUNA_CONDICAO,
          inicio: COLUNA_INICIO,
          termino: COLUNA_TERMINO,
          outlook: COLUNA_OUTLOOK,
        },
        disponivel: SITUACAO_DISPONIVEL,
        indisponivel: INDISPONIVEL_LABEL,
      },
      selecionadaId: destinoId,
      onSelecionar: selecionarObra,
      testIdPrefix: "transferir-tipo",
    },
    semObraDisponivel,
    centroCusto: {
      mostrar: centros.length > 1,
      erro: semCentroCusto
        ? SEM_CENTRO_CUSTO
        : tentouAvancar && destino !== null && centros.length > 1 && centro === null
          ? CENTRO_CUSTO_OBRIGATORIO
          : null,
      value: centroCustoId || null,
      opcoes: centros.map((item) => ({
        label: `${rotuloCentro(item)}${item.tipoJaExiste ? " · Tipo já existe" : ""}`,
        value: item.id,
      })),
      incorporacao: centro?.tipoJaExiste ? TIPO_JA_EXISTE_AJUDA : null,
      escolher: escolherCentro,
    },
    revisao: revisaoView,
    ajustar,
    revisaoEstado: {
      carregando: etapa === 2 && revisao.status === "loading",
      erro: etapa === 2 && revisao.status === "error" ? revisao.errorMessage : null,
      erroEnvio: erroRevisao,
      recarregar: revisao.reload,
    },
    destinoErro: tentouAvancar && destino === null ? DESTINO_OBRIGATORIO : null,
    proximaDesabilitada: estado !== "pronto",
    transferirDesabilitado,
    avancar: () => {
      setTentouAvancar(true);
      if (destinoEscolhido) setEtapa(2);
    },
    voltar: () => setEtapa(1),
    abrirConfirmacao: () => {
      setTentouTransferir(true);
      if (!transferirDesabilitado && !camposInvalidos) confirmacao.open();
    },
    confirmacao: {
      aberta: confirmacao.isOpen,
      mensagem:
        destino && tipo
          ? PERGUNTA_CONFIRMACAO(
              codigoTipo,
              nomeTipo,
              quantidadeEscopos,
              destino.codigo,
              embarcacao,
            )
          : "",
      avisoTransferencia: mostrarAvisoFelZero
        ? AVISO_TRANSFERENCIA_FEL_ZERO
        : AVISO_APROVACAO_TRANSFERENCIA(tipo.obraOrigem.codigo),
      justificativa: form.values.justificativa,
      justificativaError: form.errors.justificativa ?? null,
      isSubmitting: form.isSubmitting,
      submitError: form.submitError,
      digitarJustificativa: (texto: string) => form.setValue("justificativa", texto),
      enviar: (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        void form.submit();
      },
      cancelar: confirmacao.close,
    },
    cancelar,
    descarte: descarte.confirmacao,
  };
}
